import React from 'react';
import genericHelpers from '../../../_utils/generic-helpers';
import { builderDispatch } from '../../../_utils/ui';
import { UpdatePhotoEvents } from '../events/event-declarations';

import MobileFullScreen from '../../../_utils/mobile-full-screen';
import RawImageCache from '../raw-image-cache';
import CropperConvert from './cropper-convert';
import sendToGoogleAnalytics from '../../../_prism-builder/desktop/slot-tools/send-to-google-analytics';
import { FILTER_EFFECTS_OPTIONS } from '../../../_utils/advanced_editor/filter-effects-options';
import snapCropToBounds from './snap-crop-to-bounds';
import IU from '../../../_utils/img/img-utils';
import {
  MAXIMAL_ZOOM,
  MODES,
  DEFAULT_MODE,
  INITIAL_MIN_ZOOM,
} from './constants';

const { imgHelpers, zoomHelpers } = IU;

const AdvancedEditorController = WrappedComponent => {
  function extractPhotoData(source) {
    return Object.keys(source).reduce((photoData, key) => {
      if (typeof source[key] === 'function') {
        return photoData;
      }

      return { ...photoData, [key]: source[key] };
    }, {});
  }

  return class extends React.Component {
    constructor(props) {
      super(props);

      this.state = {
        imageSrc: null,
        editMode: DEFAULT_MODE,
      };
      this.data = {};
      this.initialZoom = INITIAL_MIN_ZOOM;

      this.cropperRef = null;
      this.setCropperRef = element => {
        this.cropperRef = element;
      };
      this.setZoomSliderRef = element => {
        this.zoomSliderRef = element;
      };
      this.setTiltSliderRef = element => {
        this.tiltSliderRef = element;
      };
      this.setBrightnessSliderRef = element => {
        this.brightnessSliderRef = element;
      };
      this.setDisplayEditValueRef = element => {
        this.displayEditValueRef = element;
      };

      this.debouncedOnResize = genericHelpers.debounce(this.regenerateCropper);
      this.debouncedRotate = genericHelpers.debounce(this.handleRotate);
    }

    componentDidMount() {
      $(window).on('resize orientationchange', this.debouncedOnResize);
    }

    componentWillUnmount() {
      $(window).off('resize orientationchange', this.debouncedOnResize);
    }

    getImageId = () => {
      const { photo } = this.data;

      return (photo || {}).image_id;
    };

    getDPI() {
      const values = this.values();
      values.raw_dimensions = {
        w: values.crop[IU.WIDTH],
        h: values.crop[IU.HEIGHT],
      };

      return parseInt(imgHelpers.getPhotoDPI(values), 10);
    }

    scale = () => {
      const { photo } = this.data;
      const { photo_image: image } = this.state;

      return photo.raw_dimensions.w / image.naturalWidth;
    };

    updatePhotoEdits = () => {
      const { photo } = this.data;
      const values = this.values();

      ['angle', 'crop', 'fx', 'gamma', 'straighten'].forEach(key => {
        photo[key] = values[key] || null;
      });

      return photo;
    };

    apply = save => {
      const photo = this.updatePhotoEdits();

      builderDispatch.dispatch({
        type: UpdatePhotoEvents.UPDATE,
        photo,
        hard: !!save,
      });
    };

    setData = async (data, callback) => {
      this.curCrop = undefined;

      await this.clearDidChange();
      await this.open();

      this.data = this.data || {};
      data.photo && (this.data.photo = extractPhotoData(data.photo));
      data.availableSizes && (this.data.availableSizes = data.availableSizes);
      data.sizes && (this.data.sizes = data.sizes);
      await this.loadImage();

      if (typeof this.getEffectUrls === 'function') {
        this.getEffectUrls();
      }

      if (typeof callback === 'function') {
        callback.call(this);
      }
    };

    loadImage = () => {
      return new Promise(resolve => {
        const urlProp = 'large_url';
        const photoUrl = this.data.photo[urlProp];

        RawImageCache.newGen();
        RawImageCache.cache(photoUrl);

        this.setState(
          {
            photo_image: null,
          },

          () => {
            RawImageCache.get(photoUrl, image => {
              const { photo } = this.data;
              const { image_id: imageId } = photo;
              const scale = photo.raw_dimensions.w / image.naturalWidth;

              this.curCrop = new CropperConvert(scale).crop.to(
                zoomHelpers.getCrop(photo) || zoomHelpers.defaultCrop(photo)
              );
              this.curAngle = photo.angle || 0;
              this.curEffect = photo.fx || IU.DEF_FX;

              const gamma = photo.gamma || 1;

              zoomHelpers.setBrightness(imageId, gamma * 10);
              zoomHelpers.setStraighten(imageId, photo.straighten);

              this.resetSliderValues();

              this.setState(
                {
                  photo_image: image,
                  editMode: DEFAULT_MODE,
                  imageSrc: this.generateProcessedPhotoImageSrc(image),
                },

                () => {
                  this.justLoaded = true;

                  RawImageCache.gc();

                  resolve();
                }
              );
            });
          }
        );
      });
    };

    generateProcessedPhotoImageSrc = (image, effect = this.curEffect) => {
      const {
        photo: { image_id: imageId },
      } = this.data;
      const props = {
        angle: this.curAngle,
        fx: effect,
        gamma: zoomHelpers.getBrightness(imageId) / 10,
        straighten: zoomHelpers.getStraighten(imageId),
      };

      return IU.imgHelpers.doProcessPhoto(image, props).toDataURL();
    };

    values = () => {
      const { photo } = this.data;
      const { image_id: imageId } = photo;
      const scale = this.scale();

      return {
        angle: this.curAngle,
        crop: new CropperConvert(scale).crop.from(
          this.curCrop || zoomHelpers.defaultCrop(photo)
        ),
        fx: this.curEffect,
        gamma: zoomHelpers.getBrightness(imageId) / 10,
        size_area: photo.size.area,
        straighten: zoomHelpers.getStraighten(imageId),
      };
    };

    resetSliderValues = imageId => {
      const id = imageId || this.getImageId();

      [
        {
          ref: 'zoomSliderRef',
          value: zoomHelpers.getZoom(id),
        },
        {
          ref: 'tiltSliderRef',
          value: zoomHelpers.getStraighten(id),
        },
        {
          ref: 'brightnessSliderRef',
          value: zoomHelpers.getBrightness(id),
        },
      ].forEach(sliderData => {
        if (this[sliderData.ref]) {
          this[sliderData.ref].set(sliderData.value);
        }
      });
    };

    getEffectUrls = async () => {
      const { photo_image: image } = this.state;

      if (!image) {
        return;
      }

      const promises = FILTER_EFFECTS_OPTIONS.map(({ value: effect }) => {
        return new Promise(resolve => {
          window.setTimeout(() => {
            resolve({
              effect,
              url: this.generateProcessedPhotoImageSrc(image, effect),
            });
          });
        });
      });

      const effectUrls = (await Promise.all(promises)).reduce(
        (urls, { effect, url }) => {
          urls[effect] = url;
          return urls;
        },
        {}
      );

      this.setState({
        effectUrls,
      });
    };

    open = () => {
      this.should_apply_changes = true;

      return new Promise(resolve => {
        this.setState(
          {
            open: true,
            effectUrls: undefined,
          },
          () => {
            resolve();
          }
        );
      });
    };

    close = cancelDidChange => {
      if (cancelDidChange) {
        this.did_change = false;
      }

      this.setState(
        {
          open: false,
        },
        () => {
          builderDispatch.delayedDispatch({
            type: UpdatePhotoEvents.ADVANCED_EDIT_CLOSE,
          });
        }
      );
    };

    setInitialZoom = () => {
      if (!this.cropperRef) {
        return;
      }

      // trigger a zoom event with dummy data to set the zoom slider's intial value
      const canvasData = this.cropperRef.getCanvasData();
      this.initialZoom = canvasData.width / canvasData.naturalWidth;

      // set the zoom slider's `min`
      this.forceUpdate(() => this.cropperRef.zoomTo(this.initialZoom));
    };

    clearDidChange = () => {
      return new Promise(resolve => {
        this.did_change = false;

        this.setState(
          {
            editMode: DEFAULT_MODE,
            imageSrc: undefined,
          },
          () => {
            resolve();
          }
        );
      });
    };

    regenerateCropper = () => {
      const { open } = this.state;

      if (!open) {
        return;
      }

      if (this.cropperRef) {
        this.cropperRef.cropper.destroy();
      }

      const previousDidChange = this.did_change;

      this.setData(
        {
          photo: {
            ...this.data.photo,
            ...this.values(),
          },
        },

        () => {
          this.did_change = previousDidChange;
        }
      );
    };

    handleTweakerDidChange = (
      updateProcessedPhotoImageSrc = false,
      onlyColorChanged = true
    ) => {
      this.did_change = true;

      if (!updateProcessedPhotoImageSrc) {
        return;
      }

      const { photo_image: image } = this.state;
      const { viewport } = this.props;
      const imageSrc = this.generateProcessedPhotoImageSrc(image);

      this.updatePhotoEdits();

      this.cropperRef && this.cropperRef.replace(imageSrc, onlyColorChanged);

      viewport.isMobile &&
        this.setState({
          imageSrc: imageSrc,
        });
    };

    handleEffect = value => {
      this.curEffect = value;
      this.handleTweakerDidChange(true);
    };

    handleZoom = value => {
      this.zoomSliderTriggered = true;
      this.displayEditValueRef && this.displayEditValueRef.set(value);

      if (this.cropperRef) {
        this.cropperRef.zoomTo(value);
      }
    };

    handleStraighten = value => {
      let newStraightenValue = parseFloat(value);

      if (Number.isNaN(newStraightenValue)) {
        newStraightenValue = IU.DEF_STRAIGHTEN;
      }

      zoomHelpers.setStraighten(this.getImageId(), newStraightenValue); // For Slider setting later

      this.handleTweakerDidChange(true);
    };

    handleApply = () => {
      if (this.did_change) {
        this.apply(true);
      }

      this.close();
    };

    handleCrop = e => {
      if (this.justLoaded) {
        // this will set the zoom slider to the initial value in the cropper
        // and set the slider's minmum value to it if required
        // so that there isn't a coarse jump when first sliding.
        // doing it here because the cropper has just initialized
        this.setInitialZoom();
        delete this.justLoaded;
      }

      this.curCrop = snapCropToBounds(e.detail);

      this.handleTweakerDidChange();
    };

    handleCropperZoom = e => {
      const value = e.detail.ratio;

      if (value >= MAXIMAL_ZOOM) {
        e.preventDefault();
        return false;
      }

      const { image_id: imageId } = this.data.photo;
      zoomHelpers.setZoom(imageId, value); // For Slider setting later

      if (!this.zoomSliderTriggered && this.zoomSliderRef) {
        this.displayEditValueRef && this.displayEditValueRef.set(value);
        this.zoomSliderRef.set(value);
      }

      delete this.zoomSliderTriggered;
    };

    handleRotate = ({ angle = IU.RIGHT_ANGLE }) => {
      const { photo } = this.data;

      this.curAngle = this.curAngle || 0;
      this.curAngle = (this.curAngle - angle) % 360;

      while (this.curAngle < 0) {
        this.curAngle += 360;
      }

      sendToGoogleAnalytics('el2', 'rotate');

      // reset the crop. Saves a lot of fuss
      this.curCrop = new CropperConvert(this.scale()).crop.to(
        zoomHelpers.defaultCrop(photo)
      );

      this.handleTweakerDidChange(true, false);
    };

    handleBrightness = value => {
      let newBrightnessValue = Number(value);
      if (Number.isNaN(newBrightnessValue)) {
        newBrightnessValue = IU.DEF_BRIGHT;
      }
      const {
        photo: { image_id: imageId },
      } = this.data;
      zoomHelpers.setBrightness(imageId, newBrightnessValue); // For Slider setting later

      this.handleTweakerDidChange(true);
    };

    handleReset = async () => {
      const { photo } = this.data;

      // strip most changes, but "undo" any saved straighten and brightness values
      ['angle', 'crop', 'saturation'].forEach(key => {
        photo[key] = null;
      });
      photo.straighten = IU.DEF_STRAIGHTEN; // Default Value
      photo.gamma = IU.DEF_GAMMA; // Reset to default
      photo.fx = IU.DEF_FX; // Default Value

      this.curCrop = null;
      this.curAngle = null;
      this.curEffect = photo.fx;

      zoomHelpers.setZoom(photo.image_id, null);
      zoomHelpers.setBrightness(photo.image_id, photo.gamma * 10);
      zoomHelpers.setStraighten(photo.image_id, photo.straighten);
      await this.getEffectUrls();
      await this.loadImage();
    };

    handleEditMode = editMode => {
      const selectedMode = Object.values(MODES).includes(editMode);

      if (!selectedMode) {
        return;
      }

      this.setState(
        {
          editMode,
        },
        () => {
          this.resetSliderValues();
        }
      );

      if (editMode === MODES.ROTATE) {
        this.debouncedRotate({ angle: IU.RIGHT_ANGLE });
      } else {
        this.updatePhotoEdits();
      }
    };

    render() {
      const { open, imageSrc, editMode, effectUrls } = this.state;
      const handlers = {
        handleRotate: this.debouncedRotate,
        handleApply: this.handleApply,
        handleCrop: this.handleCrop,
        handleCropperZoom: this.handleCropperZoom,
        handleBrightness: this.handleBrightness,
        handleReset: this.handleReset,
        handleEffect: this.handleEffect,
        handleZoom: this.handleZoom,
        handleStraighten: this.handleStraighten,
        close: this.close,
        handleEditMode: this.handleEditMode,
      };

      const refs = {
        cropperRef: this.setCropperRef,
        zoomSliderRef: this.setZoomSliderRef,
        tiltSliderRef: this.setTiltSliderRef,
        brightnessSliderRef: this.setBrightnessSliderRef,
        displayEditValueRef: this.setDisplayEditValueRef,
      };

      const editorProps = {
        ...this.data,
        curCrop: this.curCrop,
        imageSrc,
        editMode,
        effectUrls,
        initialZoom: this.initialZoom,
        open,
      };

      return (
        <MobileFullScreen
          className="image-slot-editor advanced-editor"
          open={!!open}
          onBack={this.close}
          noHeader
          fullWidth>
          <WrappedComponent refs={refs} handlers={handlers} {...editorProps} />
        </MobileFullScreen>
      );
    }
  };
};

export default AdvancedEditorController;
